<script setup lang="ts">
import SvgIcon from "@jamescoyle/vue-icon"
import { mdiFormatListBulleted } from "@mdi/js"
import { computed, onBeforeUnmount, provide, ref, useSlots, watch } from "vue"
import { useLgBreakpoint } from "../../composables/useLgBreakpoint"
import { FormContextKey, type ConflictChoice, type FieldConflict, type FormApi } from "../types"
import FormToc from "./FormToc.vue"
import FormSectionSheet from "./FormSectionSheet.vue"
import DirtySaveToast from "./DirtySaveToast.vue"
import SaveConflictDialog from "./SaveConflictDialog.vue"
import type { EditSessionApi } from "../composables/useEditSession"
import EditSessionBanner from "./EditSessionBanner.vue"

const props = withDefaults(defineProps<{
  form: FormApi<unknown>
  /** Page-level heading. Rendered as a visually-hidden <h1> for the document outline. */
  title?: string
  /** Forwarded to <DirtySaveToast>: when true, hides the save toast and disables its Ctrl/Cmd+S shortcut. */
  saveDisabled?: boolean
  /**
   * Read-only mode: wraps the default slot in a disabled <fieldset>, which natively disables
   * every form control inside — including ones added later. Also implies saveDisabled.
   * Does NOT reach contenteditable (TipTap) or triggers that are not <button>; those bind it
   * themselves.
   */
  readOnly?: boolean
  /**
   * „Wird gerade bearbeitet von" (useEditSession): zeigt das Banner und schaltet im
   * Lese-Modus der Sitzung zusätzlich readOnly.
   */
  editSession?: EditSessionApi
}>(), {
  saveDisabled: false,
  readOnly: false
})

const effectiveReadOnly = computed(() => props.readOnly || !!props.editSession?.readOnly.value)
const saveIsDisabled = computed(() => props.saveDisabled || effectiveReadOnly.value)

provide(FormContextKey, props.form)

const conflictOpen = ref(false)
const conflictItems = ref<FieldConflict[]>([])
let settle: ((choices: Record<string, ConflictChoice> | null) => void) | null = null

props.form.setConflictResolver((conflicts) => new Promise((resolve) => {
  conflictItems.value = conflicts
  conflictOpen.value = true
  settle = resolve
}))
onBeforeUnmount(() => {
  // Ein offener Dialog beim Verlassen (z.B. Route-Leave-Bestaetigung) darf die
  // wartende save()/mergeWith()-Kette nicht haengen lassen: erst als Abbruch
  // aufloesen, dann den Resolver abmelden.
  finishConflict(null)
  props.form.setConflictResolver(null)
})

function finishConflict(choices: Record<string, ConflictChoice> | null) {
  conflictOpen.value = false
  settle?.(choices)
  settle = null
}

const isLg = useLgBreakpoint()
const slots = useSlots()
const sheetOpen = ref(false)

const hasSections = computed(() => props.form.sections.value.length > 0)
const hasActions = computed(() => Boolean(slots["toc-footer"]))
const showMobileBar = computed(() => hasSections.value || hasActions.value)

// Beim Wechsel auf Desktop verschwindet der Auslöser — ein offenes Sheet mit.
watch(isLg, (lg) => {
  if (lg) sheetOpen.value = false
})
</script>

<template>
  <div class="lg:bg-base-200 lg:rounded-2xl lg:p-6 lg:pb-32">
    <h1 v-if="title" class="sr-only">{{ title }}</h1>

    <EditSessionBanner
      v-if="editSession"
      :mode="editSession.mode.value"
      :others="editSession.others.value"
      @take-over="editSession.takeOver()"
    />

    <!--
      Mobil (< lg): klebende Leiste unter dem 4rem-Header. Der untere Bildschirmrand
      ist durch AppDock und DirtySaveToast belegt, deshalb sitzt sie oben.
    -->
    <div
      v-if="!isLg && showMobileBar"
      data-testid="form-mobile-bar"
      class="sticky top-16 z-10 mb-3 flex items-center gap-2 border-b border-base-300 bg-base-100 px-4 py-2"
    >
      <button
        v-if="hasSections"
        type="button"
        data-testid="form-mobile-sections-trigger"
        class="btn btn-ghost btn-sm gap-2"
        aria-haspopup="dialog"
        :aria-expanded="sheetOpen"
        @click="sheetOpen = true"
      >
        <SvgIcon type="mdi" :path="mdiFormatListBulleted" class="size-4" />
        <span>Abschnitte</span>
      </button>
      <div class="ml-auto w-48 max-w-[60%]">
        <slot name="toc-footer" />
      </div>
    </div>

    <div class="lg:grid lg:grid-cols-[16rem_1fr] lg:gap-6">
      <!--
        Breakpoint-abhängig, NICHT per CSS versteckt: der toc-footer-Slot darf nur
        einmal existieren, sonst gibt es EntityDeleteAction (und dessen
        Bestätigungsdialog) doppelt.
      -->
      <aside v-if="isLg" class="space-y-4">
        <FormToc />
        <slot name="toc-footer" />
      </aside>
      <div class="space-y-3 lg:space-y-4 min-w-0">
        <fieldset :disabled="effectiveReadOnly" class="space-y-3 lg:space-y-4 min-w-0 contents">
          <slot />
        </fieldset>
      </div>
    </div>

    <FormSectionSheet v-if="!isLg" v-model:open="sheetOpen" />
    <DirtySaveToast :disabled="saveIsDisabled" />
    <SaveConflictDialog :open="conflictOpen" :conflicts="conflictItems" @resolve="finishConflict" @cancel="finishConflict(null)" />
  </div>
</template>
